import {Injectable} from '@angular/core';
import {Storage} from "@ionic/storage";
import {ApiBoxProvider} from "../api-box/api-box";
import {Alert, AlertController, Events, Platform} from "ionic-angular";
import {ApiRestProvider} from "../api-rest/api-rest";
import {Box, ScanTypes, WifiInfo} from "../../model/weak-entities";
import {BoxStatus, ClaAudio, ClaEvents, ClaStorage} from "../../model/events";
import {DEFAULT_INTERRUPTSOURCES, Idle} from "@ng-idle/core";
import {Keepalive} from "@ng-idle/keepalive";
import * as moment from 'moment';
import {Doctor} from "../../model/doctor";
import {ExamProvider} from "../exam/exam";
import {delay} from "../../utils/utils";
import {BleGear, bluetoothMode, Practice} from "../../model/practice";
import {SecurityStorageService} from "../security-storage/security-storage-service";
import {LogService} from "../logging/log.service";

const OFFLINE_BOX_IP = "192.168.137.1";
//const OFFLINE_BOX_IP = "192.168.200.44";
@Injectable()
export class AppStateProvider {
  public bluetooth: bluetoothMode = bluetoothMode.noBT;
  public allGear: BleGear[] = [];
  public storedGear = [];
  public allBoxes: Box[] = [];
  public boxes: Box[] = [];
  public isIpad: Boolean = false;
  private _activeBoxId: string;
  private token: string;
  public get activeBox():Box {
    if(this.boxes === undefined) return;
    return this.boxes.filter(b => b.DeviceId === this._activeBoxId)[0]
  }
  private _activeBoxStatus: BoxStatus;

  public get activeBoxStatus(): BoxStatus { return this._activeBoxStatus; }
  public set activeBoxStatus(status: BoxStatus) {
    this._activeBoxStatus = status;
    this.events.publish(ClaEvents.BOX_STATUS_CHANGE, status);
  }

  private _loginAllowed: boolean;
  public get loginAllowed(): boolean { return this._loginAllowed; }
  public set loginAllowed(status: boolean) {
    this.logger.debug(`login allowed ${status}`, [{'SourceContext': 'AppStateProvider'}, {'Method': 'loginAllowed set'}]);
    if(this._loginAllowed == status) return;

    this._loginAllowed = status;
    this.events.publish(ClaEvents.LOGIN_STATUS_CHANGE, status);
  }


  public activeDoctor: Doctor;
  public activePractice: Practice;

  // This will keep the state of if the active user is a doctor or examiner
  public activeUserIsDoctor: boolean = false;
  public bluetoothOffline: boolean = false;

  public scanningEnabled: boolean;
  public serverOnline: boolean;
  public onboarding: boolean;
  public offlineMode: boolean = false;
  public onlineLocalMode: boolean = false;
  private onlineLocalCounter: number = 0;

  constructor(public apiBox: ApiBoxProvider,
              public api: ApiRestProvider,
              public storage: Storage,
              public events: Events,
              public idle: Idle,
              public platform: Platform,
              public keepalive: Keepalive,
              private exam: ExamProvider,
              private alertCtrl: AlertController,
              private logger: LogService,
              public securityStorageService: SecurityStorageService) {
    this.logger.debug('start', [{'SourceContext': 'AppStateProvider'}, {'Method': 'Constructor'}]);
    this.isIpad = document.URL.startsWith('ionic');
    this.activeBoxStatus = BoxStatus.UNKNOWN;
    this.scanningEnabled = false;
    this.onboarding = false;
    this.loginAllowed = false;
    this.offlineMode = false;
    this.apiBox.offlineMode = false;

    // Set idle timeout to 45 minutes
    idle.setIdle(60*60*4);

    // Set timeout to 60 seconds. This is how long they have to respond after going idle
    idle.setTimeout(60);

    // Use default interupts like touches / taps / clicks / scrolls
    // TODO: Add scanning / onboard as an interrupt
    idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

    // Temporary hooks for idle states
    idle.onIdleEnd.subscribe(() => this.stopIdle());
    idle.onIdleStart.subscribe(() => this.goIdle());

    idle.onTimeout.subscribe(() => this.timeOut());

    idle.watch(true);
    idle.stop();

    // Keepalive will ping the box while we are connected, and when we stop being idle
    keepalive.interval(15);

    // Ping box
    keepalive.onPing.subscribe(() => this.activeBox ? this.checkBox() : this.checkOnline(0));

    this.events.subscribe(ClaEvents.BOX_DISCONNECT, async () => {
      await this.retryBox();
    });

    this.events.subscribe(ClaEvents.APP_RESUME, async () => {
      if (!this.onboarding) {
        if(this.apiBox.scanningState != null) {
          await this.apiBox.completeScan(this.apiBox.scanningState)
        }
        if(this.isIpad) {
          await this.exam.logOut();
          this.logger.debug(`APP RESUME`, [{'SourceContext': 'AppStateProvider'}, {'Method': '[EVENT]APP_RESUME'}]);
          await this.logOut();
          await this.evaluateState();
        }
      }
    });

    this.api.LocalOnlineState.subscribe(state => {
      if(state) {
        this.onlineLocalMode = true;
        this.onlineLocalCounter = 0;
        this.activeBoxStatus = BoxStatus.ONLINE_LOCAL;
      }
    });

    this.clearOffline();
  }

  public async logOut() {
    this.logger.debug(`LOGOUT REQUEST`, [{'SourceContext': 'AppStateProvider'}, {'Method': 'logOut'}]);
    this.setTimeoutEnabled(false);
    await this.apiBox.disconnectFromBox();
    this._activeBoxId = null;
    if(this.boxes != undefined) {
      this.boxes.length = 0;
    }
    this._activeBoxStatus = BoxStatus.UNKNOWN;
    this.scanningEnabled = false;
    this.onboarding = false;
    this.loginAllowed = false;
    this.offlineMode = false;
    this.activeDoctor = null;
    this.activePractice = null;
    this.apiBox.offlineMode = false;
    await this.storage.remove(ClaStorage.APP_LOGIN_TOKEN);
    this.token = null;
    if(this.bluetoothOffline) {
      this.events.publish(ClaEvents.NAV_APP_ROOT, 'LoginPage');
      this.bluetoothOffline = false;
    }
  }

  public async clearOffline() {
    await this.storage.remove(ClaStorage.APP_OFFLINE_STAMP);
  }

  public async changeThermalRollingState(state: boolean) {
    await this.storage.set(ClaStorage.THERMAL_ROLLING_SETTING,state);
  }

  public async getThermalSettingsState(): Promise<boolean> {
    let state = await this.storage.get(ClaStorage.THERMAL_ROLLING_SETTING);
    if(state == undefined) {
      await this.changeThermalRollingState(false);
      return false;
    }
    return state;
  }

  public async changeSounds(state: string) {
    await this.storage.set(ClaStorage.APP_SOUNDS_STATE,state);
  }

  public async getSoundState(): Promise<string> {
    let state = await this.storage.get(ClaStorage.APP_SOUNDS_STATE);
    if(state == undefined) {
      await this.changeSounds(ClaAudio.MALE);
      return ClaAudio.MALE.toString();
    }
    return state;
  }

  // Tells the app to prevent timeouts
  public setTimeoutEnabled(state: boolean) {
    if (state) {
      this.logger.debug(`enabling idle timeout`, [{'SourceContext': 'AppStateProvider'}, {'Method': 'setTimeoutEnabled'}]);
      this.idle.watch(true);
    } else {
      this.logger.debug(`disabling idle timeout`, [{'SourceContext': 'AppStateProvider'}, {'Method': 'setTimeoutEnabled'}]);
      this.idle.stop();
    }
  }

  // Tells the app to stop pinging the box
  public setKeepAliveEnabled(state: boolean) {
    if (state) {
      this.keepalive.start();
    } else {
      this.keepalive.stop();
    }
  }

  // Sets app into offline mode for 30 days
  public async goOffline() {
    // TODO: Verify user account is in good standing

    this.logger.debug(`go offline`, [{'SourceContext': 'AppStateProvider'}, {'Method': 'goOffline'}]);
    await this.logOut();
    // Store the current date into storage
    await this.storage.set(ClaStorage.APP_OFFLINE_STAMP, Date.now());

    // Reset the app
    await this.evaluateState();
  }

  // Sets app back into online mode
  public async goOnline() {
    // TODO: Verify user account is in good standing
    // TODO: Allow user to return to offline mode

    // Clear offline storage
    await this.storage.remove(ClaStorage.APP_OFFLINE_STAMP);

    // Reset the app
    await this.evaluateState();
  }

  public goIdle() {
    // Go to home
    this.events.publish(ClaEvents.NAV_GO_HOME);

    // Toast warning
    this.events.publish(ClaEvents.TOAST_WARNING, 'App will logout due to inactivity in 1 minute');
  }

  public stopIdle() {
    // Toast hide
    this.events.publish(ClaEvents.TOAST_HIDE);
  }

  public async timeOut() {
    await this.exam.logOut();

    this.events.publish(ClaEvents.TOAST_HIDE);

    await this.storage.remove(ClaStorage.APP_PREVIOUS_BOX_ID);


    await this.exam.logOut();
    await this.logOut();

    await this.evaluateState();
  }

  public async checkBox() {
    if (this.onboarding) return;
    if (!this.activeBox) return;

    // If the box is online, double check it
    if (this.activeBoxStatus == BoxStatus.ONLINE_ALL || this.activeBoxStatus == BoxStatus.CONNECTING) {

      // Ping box locally
      let localOnline = await this.api.pingBoxLocal(this.activeBox, this.offlineMode);
      if(!localOnline) {
        localOnline = await this.api.pingBoxLocalIp(this.activeBox, this.offlineMode);
      }
      // let localOnline = true;

      // If box is offline, handle
      if (!localOnline && !this.apiBox.isReconnecting) {
        this.logger.debug(`box ${this.activeBox.DeviceId} local pings failed (${this.activeBox.IpAddress}), disconnecting`, [{'SourceContext': 'AppStateProvider'}, {'Method': 'checkBox'}]);
        await this.apiBox.disconnectFromBox();
      }

    } else if (this.activeBoxStatus == BoxStatus.ONLINE_AZURE) {

      // If the box os online with the server only

      // Get our latest boxes
      await this.refreshBoxes();

      // If the box is now offline in azure, switch state
      if (!this.activeBox.Connected) {

        // Box is offline completely
        this.activeBoxStatus = BoxStatus.OFFLINE_ALL;

      } else {
        // Ping box locally
        let localOnline = await this.api.pingBoxLocal(this.activeBox, this.offlineMode);
        if(!localOnline) {
          localOnline = await this.api.pingBoxLocalIp(this.activeBox, this.offlineMode);
        }
        // let localOnline = true;

        // If the box is reachable again, trigger a reconnect
        if (localOnline) {

          await this.connectToBox(this.activeBox);
        }
      }
    } else if (this.activeBoxStatus == BoxStatus.OFFLINE_ALL) {

      // Get our latest boxes
      await this.refreshBoxes();

      if (this.activeBox.Connected) {
        await this.connectToBox(this.activeBox);
      }
    } else if (this.activeBoxStatus == BoxStatus.ONLINE_LOCAL) {
      this.onlineLocalCounter++;
      if(this.onlineLocalCounter < 5) return;

      this.onlineLocalCounter = 0;

      // first check for online access
      let onlineState = await this.api.pingCloud();
      if(!onlineState) return;
      // next check if box is done syncing

      await this.api.useCloud();
      this.onlineLocalMode = false;
      this.activeBoxStatus = BoxStatus.ONLINE_ALL;
    }
  }

  public tryShowHelpModal() {
    if (this.onboarding) return;
    if (!this.activeBox) return;

    if (this.activeBoxStatus == BoxStatus.OFFLINE_ALL || this.activeBoxStatus == BoxStatus.ONLINE_AZURE) {
      // Show help modal
      this.events.publish(ClaEvents.MODAL_BOX_STATUS);
    }
  }

  public tryShowBoxSelectModal() {
    if (this.boxes.length > 1) {
      // Show select modal
      this.events.publish(ClaEvents.MODAL_BOX_SELECT);
    }
  }

  public async refreshBoxes() {
    if(this.offlineMode) {
      return;
    }
    if(!this.loginAllowed) {
      return;
    }
    // Get the users boxes
    // Online Mode
    try {
      this.boxes = await this.api.getBoxes().toPromise();
    }
    catch (e) {

      this.logger.debug(`error refreshing boxes ${JSON.stringify(e)}`, [{'SourceContext': 'AppStateProvider'}, {'Method': 'refreshBoxes'}]);
      return;
    }
  }

  // Kickoff method to evaluate the state of the app
  public async evaluateState(noRedirect?:boolean) {
    try
    {
      // Disable login
      this.loginAllowed = false;

      // Check if we are in offline mode
      let offlineStamp = await this.storage.get(ClaStorage.APP_OFFLINE_STAMP);

      // If we are, switch to offline mode
      if (offlineStamp) {
        await this.doOffline(offlineStamp, noRedirect);

        return;
      }
      if (this.bluetoothOffline) {
        if(this.offlineMode) return;
        this.storedGear = await this.storage.get(ClaStorage.BLE_GEAR);
        this.bluetooth = bluetoothMode.activeBT;
        this.events.publish(ClaEvents.NAV_APP_ROOT, 'SplashLogoPage');
        this.offlineMode = true;
        return;
      }
      // We are not in offline mode
      this.offlineMode = false;
      this.apiBox.offlineMode = false;

      // Switch to offline api
      await this.api.useCloud();

      // Check if we can reach cloud server
      let count = 0;
      do {
        await delay(1000);
        this.serverOnline = await this.api.pingServer();
        count++;
      } while(!this.serverOnline && count < 3);

      if (this.serverOnline) {

        // Enable login
        this.loginAllowed = true;

        // If the server is online
        const token = await this.securityStorageService.getSecurityToken()
        this.token = token;
        // If we have redirection enabled:
        if (!noRedirect) {
          // If they are not logged in, kick to login page
          if (!token) {
            this.logger.debug(`token not found kicking to login`, [{'SourceContext': 'AppStateProvider'}, {'Method': 'evaluateState'}]);
            this.events.publish(ClaEvents.NAV_APP_ROOT, 'LoginPage');
            return;
          } else {
            this.events.publish(ClaEvents.NAV_APP_ROOT, 'SplashLogoPage');
            return;
          }
        }

        //await delay(50);

        // Start the idle watcher

        // Get our latest boxes
        await this.refreshBoxes();

        // bluetooth state
        await this.checkBluetoothState();

        // If we have no boxes on our account, we are a new account
        // SPECIAL: If they are app store, kick them into main app
        if (this.activeDoctor != null && this.activeDoctor.Email == 'app@pulseb2b.com') {

          // If they have not done walkthrough do that first
          let walkthroughDone = 'false';
          if(this.isIpad) {
            walkthroughDone = await this.storage.get(ClaStorage.APP_WALKTHROUGH_DONE);
          } else {
            walkthroughDone = await localStorage.getItem(ClaStorage.APP_WALKTHROUGH_DONE);
          }

          if (!walkthroughDone) {
            this.events.publish(ClaEvents.NAV_WALKTHROUGH);
            return;
          }

        } else if (!this.isIpad && this.allBoxes.length === 0 && this.bluetooth === bluetoothMode.noBT) {

          // Browser with no bridges (XLE-only): onboarding is iPad-only, so skip the welcome pages /
          // "Onboard Not Available" alert and go straight to browser scan-view mode (CS-121)
          this.bluetooth = bluetoothMode.browserBT;
          await this.storage.set(ClaStorage.BLUETOOTH, this.bluetooth);
          await this.handleBluetooth();

          return;
        } else if (this.boxes.length < 1 && this.bluetooth === bluetoothMode.noBT) {

          // Send us to the welcome pages
          this.events.publish(ClaEvents.NAV_WELCOME);

          return;
        } else if (this.boxes.length === 1 && this.bluetooth === bluetoothMode.noBT) {
          if (this.boxes[0].Name.includes('INSiGHT Bridge')) {
            this.events.publish(ClaEvents.NAV_CONTINUE_ONBOARD, {page: 'BoxStep5Page', box: this.boxes[0]});
            return;
          }
          // If they have not done walkthrough do that first
          let walkthroughDone = 'false';
          if(this.isIpad) {
            walkthroughDone = await this.storage.get(ClaStorage.APP_WALKTHROUGH_DONE);
          } else {
            walkthroughDone = await localStorage.getItem(ClaStorage.APP_WALKTHROUGH_DONE);
          }

          if (!walkthroughDone) {
            this.events.publish(ClaEvents.NAV_WALKTHROUGH);
            return;
          }

          // If we only have one box, Just use that one
          await this.connectToBox(this.boxes[0]);
          this.checkBoxGear(this.boxes[0]);
          return;
        } else if (this.boxes.length < 1 && this.bluetooth === bluetoothMode.noBT) {
          // If they have not done walk through do that first
          let walkthroughDone = 'false';
          if(this.isIpad) {
            walkthroughDone = await this.storage.get(ClaStorage.APP_WALKTHROUGH_DONE);
          } else {
            walkthroughDone = await localStorage.getItem(ClaStorage.APP_WALKTHROUGH_DONE);
          }

          if (!walkthroughDone) {
            this.events.publish(ClaEvents.NAV_WALKTHROUGH);
            return;
          }

          return;
        } else if (this.bluetooth !== bluetoothMode.noBT) {
          await this.handleBluetooth();
          return;
        } else {
          // If they have not done walkthrough do that first
          let walkthroughDone = 'false';
          if(this.isIpad) {
            walkthroughDone = await this.storage.get(ClaStorage.APP_WALKTHROUGH_DONE);
          } else {
            walkthroughDone = await localStorage.getItem(ClaStorage.APP_WALKTHROUGH_DONE);
          }

          if (!walkthroughDone) {
            this.events.publish(ClaEvents.NAV_WALKTHROUGH);
            return;
          }
          // If we have more than one box, lets see if we saved a box previously
          let savedBoxId = await this.storage.get(ClaStorage.APP_PREVIOUS_BOX_ID);
          // If there is a saved box
          if (savedBoxId) {
            // Try to get the saved box
            let savedBox = this.boxes.filter(b => b.DeviceId === savedBoxId)[0];
            // If it does exist, use it
            if (savedBox) {
              await this.connectToBox(savedBox);
              if(!this.checkBoxGear(savedBox)) {
                this.events.publish(ClaEvents.NAV_GO_HOME);
              }
              return;
            }
          }
          // If there is no saved box, or the box that was saved does not exist for some reason
          this.events.publish(ClaEvents.MODAL_BOX_SELECT);
        }
      } else {
        let iOSAvailable = false;
        if (this.isIpad) {
          iOSAvailable = true;
        }
        let offlineMsg = this.alertCtrl.create({
          title: 'No Internet Connection Detected',
          message: 'Still want to scan?',
          buttons: [
            {
              text: 'Use INSiGHT Bridge Screening Mode',
              handler: () => {this.events.publish(ClaEvents.NAV_APP_ROOT, 'OfflineStep1Page');}
            },
            {
              text: 'Try Connection Again',
              handler: () => {this.evaluateState();}
            }
          ]
        });
        const gear = await this.storage.get(ClaStorage.BLE_GEAR);
        if(gear) {
          offlineMsg = this.alertCtrl.create({
            title: 'No Internet Connection Detected',
            message: 'Still want to scan?',
            buttons: [
              {
                text: 'Use INSiGHT Bridge Screening Mode',
                handler: () => {this.events.publish(ClaEvents.NAV_APP_ROOT, 'OfflineStep1Page');}
              },
              {
                text: ' Use Synapse XLE Offline Mode',
                handler: () => {
                  this.loginAllowed = true;
                  this.events.publish(ClaEvents.NAV_APP_ROOT, 'LoginPage');
                  this.bluetoothOffline = true;
                }
              },
              {
                text: 'Try Connection Again',
                handler: () => {this.evaluateState();}
              }
            ]
          });
        }

        if(iOSAvailable == false) {
          this.logger.debug(`not iPad ${iOSAvailable}`, [{'SourceContext': 'AppStateProvider'}, {'Method': 'evaluateState'}]);
          offlineMsg = this.alertCtrl.create({
            title: 'No Internet Connection Detected',
            message: 'Please verify your network connection',
            buttons: [{
              text: 'Try Connection Again',
              handler: () => {this.evaluateState();}
            }]
          });
        }
        await offlineMsg.present();
      }
    }
    catch (e) {
      this.logger.error(`error in evaluate state ${JSON.stringify(e)}`, [{'SourceContext': 'AppStateProvider'}, {'Method': 'evaluateState'}]);
    }
  }

  //region bluetooth
  private async checkBluetoothState() {
    // get all boxes even the non onboarded
    this.allBoxes = await this.api.getAllBoxes().toPromise();
    // load all ble gear
    const storedGear = await this.storage.get(ClaStorage.ALL_BLE_GEAR);
    this.allGear = storedGear ? storedGear : [];
    // get stored ble state
    const bleStores = await this.storage.get(ClaStorage.BLUETOOTH);
    // set storage if it's there
    if (bleStores) {
      this.bluetooth = bleStores;
      // no need to go on from here only ipads will store it
      return;
    }
    // get stored bridge
    let savedBoxId = await this.storage.get(ClaStorage.APP_PREVIOUS_BOX_ID);
    // set bluetooth to not active if no saved box id, there is tech and no boxes
    (!savedBoxId && this.allGear.length !== 0 && this.allBoxes.length === 0) && (this.bluetooth = bluetoothMode.notActiveBT);
    // save gear on initial start up (first log in)
    (this.isIpad && this.allGear.length > 0) && await this.checkBleTech();
  }

  private async handleBluetooth() {
    // screening mode
    if (this.offlineMode && !this.bluetoothOffline) {
      await this.connectToBox(this.boxes[0]);
      this.bluetooth = bluetoothMode.noBT;
      await this.storage.remove(ClaStorage.BLUETOOTH);
      return;
    }
    // if ipad and no gear go set up gear and no bridges
    const noGear = this.allGear.length === 0;
    if (noGear && this.isIpad && this.bluetooth === bluetoothMode.activeBT) {
      this.events.publish(ClaEvents.NAV_CONTINUE_BLUETOOTH);
      return;
    }
    if (noGear && this.isIpad && this.boxes.length === 0) {
      this.bluetooth = bluetoothMode.activeBT;
      await this.storage.set(ClaStorage.BLUETOOTH, this.bluetooth);
      this.events.publish(ClaEvents.NAV_CONTINUE_BLUETOOTH);
      return;
    }
    // WALK THROUGH if not done
    let walkthroughDone = 'false';
    if(this.isIpad) {
      walkthroughDone = await this.storage.get(ClaStorage.APP_WALKTHROUGH_DONE);
    } else {
      walkthroughDone = await localStorage.getItem(ClaStorage.APP_WALKTHROUGH_DONE);
    }

    if (!walkthroughDone) {
      this.events.publish(ClaEvents.NAV_WALKTHROUGH);
      return;
    }

    // save default tech and enable scanning for just ipads
    if (this.isIpad) {
      this.bluetooth = bluetoothMode.activeBT;
      await this.storage.set(ClaStorage.BLUETOOTH, this.bluetooth);
      await this.checkBleTech();
      !noGear && !this.activeDoctor.ScanningDisabled && (this.scanningEnabled = true);
    } else {
      // set browser to allow scan views only
      this.bluetooth = bluetoothMode.browserBT;
      await this.storage.set(ClaStorage.BLUETOOTH, this.bluetooth);
    }
  }

  public async checkBleTech() {
    // data is [EMG, Thermal, HRV]
    this.storedGear = await this.storage.get(ClaStorage.BLE_GEAR);
    if (this.allGear.length < 1) {
      this.storedGear && await this.storage.remove(ClaStorage.BLE_GEAR);
      this.storedGear = ['', '', ''];
      return;
    }
    await this.storage.set(ClaStorage.ALL_BLE_GEAR, this.allGear);
    const emg = this.allGear.filter(x => this.stringToScan(x.Type) === ScanTypes.EMG)[0];
    const thermal = this.allGear.filter(x => this.stringToScan(x.Type) === ScanTypes.Thermal)[0];
    const hrv = this.allGear.filter(x => this.stringToScan(x.Type) === ScanTypes.HRV)[0];

    const saveDefault = [
      (emg? emg.BleId : ''),
      (thermal? thermal.BleId : ''),
      (hrv? hrv.BleId : '')
    ];
    this.allBoxes.length > 0 && saveDefault.push('bridge');

    if (!this.storedGear) {
      await this.storage.set(ClaStorage.BLE_GEAR, saveDefault);
      this.storedGear = saveDefault;
      return;
    }

    const addNew = [
      (this.checkBleValid(this.storedGear[0]) ? this.storedGear[0] : saveDefault[0]),
      (this.checkBleValid(this.storedGear[1]) ? this.storedGear[1] : saveDefault[1]),
      (this.checkBleValid(this.storedGear[2]) ? this.storedGear[2] : saveDefault[2])
    ];
    this.allBoxes.length > 0 && addNew.push('bridge');
    this.storedGear && await this.storage.set(ClaStorage.BLE_GEAR, addNew);
    this.storedGear = addNew;
  }

  public stringToScan(type) {
    let scan = ScanTypes.EMG;
    switch (type)
    {
      case 'Thermal':
      case ScanTypes.Thermal:
        scan = ScanTypes.Thermal;
        break;
      case 'EMG':
      case ScanTypes.EMG:
        scan = ScanTypes.EMG;
        break;
      case 'HRV':
      case ScanTypes.HRV:
        scan = ScanTypes.HRV;
        break;
    }
    return scan;
  }

  private checkBleValid(id: string) {
    return this.allGear.filter(x => x.BleId === id).length > 0;
  }

  public async activateBluetooth() {
    await this.apiBox.disconnectFromBox(true);
    await this.storage.remove(ClaStorage.APP_PREVIOUS_BOX_ID);
    this._activeBoxId = '';
    this.bluetooth = bluetoothMode.activeBT;
    await this.storage.set(ClaStorage.BLUETOOTH, bluetoothMode.activeBT);
    if(!this.activeDoctor.ScanningDisabled) this.scanningEnabled = true;
    await this.checkBleTech();
  }

  public async checkOnline(count: number) {
    // if you logged in with bluetooth mode just return
    if(!this.token) return;
    // check online only when logged in
    let onlineState = await this.api.pingCloud();
    // prevent alert when one ping fails only
    if(!onlineState && count < 5) {
      let c = count ? count : 0;
      setTimeout(async () => {
        await this.checkOnline(c + 1);
      }, 1000)
      return
    }
    // goes offline ipad only
    (!onlineState && this.isIpad && !this.bluetoothOffline) && await this.goBluetoothOffline(true);
    // goes online ipad only
    (onlineState && this.isIpad && this.bluetoothOffline) && await this.goBluetoothOffline(false);
  }

  public async goBluetoothOffline(offline: boolean) {
    // stops from trying to go online to prevent a broken app only goes online when logged in correctly
    this.offlineMode = offline;
    this.bluetoothOffline = offline;
    this.scanningEnabled = !offline;
    offline && await this.offlineAlert();
  }

  private async offlineAlert() {
    const alert = await this.alertCtrl.create({
      title: 'No Internet Connection',
      subTitle: 'Only Quick Scan is available',
      buttons: [
        {
          text: 'Ok',
          role: 'Ok',
        }
      ]
    });
    await alert.present();
  }
  //endregion

  public checkBoxGear(box: Box):boolean {
    if (!box.Emg && !box.Hrv && !box.Thermal && !box.Rom && box.Connected) {
      this.events.publish(ClaEvents.NAV_CONTINUE_ONBOARD, {page: 'GearStep1Page', box: box});
      return true;
    }
    return false;
  }

  public async doOffline(offlineStamp: Date, noRedirect?: boolean) {
    this.logger.debug(`starting offline timestamp: ${offlineStamp}, redirect: ${noRedirect}`, [{'SourceContext': 'AppStateProvider'}, {'Method': 'doOffline'}]);

    let offlineBox = <Box>{
      DeviceId: "OFFLINEBOX",
      LocationId: "",
      Ssid: `${WifiInfo.SSID}-XXX`,
      IpAddress: OFFLINE_BOX_IP,
      Name: "Screening Mode",
      Connected: true,
      Thermal: true,
      ThermalMac: "00:00:00:00:00:00",
      Hrv: true,
      HrvMac: "00:00:00:00:00:00",
      Emg: true,
      EmgMac: "00:00:00:00:00:00",
      Rom: false,
      RomMac: "00:00:00:00:00:00",
      Algo: false,
      AlgoMac: "00:00:00:00:00:00"
    };
    // This is our evaluate state if we are set into offline mode

    // We are in offline mode
    this.offlineMode = true;
    this.apiBox.offlineMode = true;

    this.api.useOffline();

    // Use moment
    let offline = moment(offlineStamp);
    //let offline = moment('5/5/2018');

    // Check they still have time
    // If they are expired
    if (offline.add(30, 'days') < moment()) {
      this.logger.debug(`offline timestamp timeout`, [{'SourceContext': 'AppStateProvider'}, {'Method': 'doOffline'}]);
      this.events.publish(ClaEvents.NAV_APP_ROOT, 'LoginPage');

      setTimeout(() => this.events.publish(ClaEvents.ALERT_OFFLINE_EXPIRED), 500);

      return;
    }

    this.events.publish(ClaEvents.OFFLINE_CONNECTING);

    // If they are still good, ping to make sure they are on WFD
    // Ping box locally
    let localOnline = false;
    let count = 0;
    do {
      localOnline = await this.api.pingBoxLocal(offlineBox,this.offlineMode);
      if(!localOnline) {
        localOnline = await this.api.pingBoxLocalIp(offlineBox,this.offlineMode);
      }
      await delay(1000);
      count++;
    } while(!localOnline && count < 3);

    this.events.publish(ClaEvents.OFFLINE_CONNECTING_COMPLETE);

    if (!localOnline) {
      this.events.publish(ClaEvents.ALERT_OFFLINE_FAILED);
      return;
    } else {
      // Enable login
      this.loginAllowed = true;

      let token = await this.storage.get(ClaStorage.APP_LOGIN_TOKEN);

      if (!noRedirect) {
        // If they are not logged in, kick to login page
        if (!token) {
          this.events.publish(ClaEvents.NAV_APP_ROOT, 'LoginPage');

          return;
        } else {
          this.events.publish(ClaEvents.NAV_APP_ROOT, 'SplashLogoPage');

          return;
        }
      }

      this.setTimeoutEnabled(true);

      this.scanningEnabled = false;

      this.boxes = [offlineBox];
      this._activeBoxId = offlineBox.DeviceId;
      let boxConnected = await this.apiBox.connectToBox(offlineBox);
      if(boxConnected) {
        try{
          let deviceInfo = await this.apiBox.getDeviceInfo();
          // Update box
          offlineBox.EmgMac = deviceInfo.emgMac;
          offlineBox.EmgFirmware = deviceInfo.emgFirmware;
          offlineBox.HrvMac = deviceInfo.hrvMac;
          offlineBox.HrvFirmware = deviceInfo.hrvFirmware;
          offlineBox.ThermalMac = deviceInfo.thermalMac;
          offlineBox.ThermalSyncLeft = deviceInfo.thermSyncLeft;
          offlineBox.ThermalSyncRight = deviceInfo.thermSyncRight;
          offlineBox.RomMac = deviceInfo.romMac;
          offlineBox.AlgoMac = deviceInfo.algoMac;
          offlineBox.CurrentVersion = deviceInfo.currentVersion;
          offlineBox.UpdateAvailable = deviceInfo.updateAvailable;
          offlineBox.UpdateVersionAvailable = deviceInfo.updateVersionAvailable;
          offlineBox.EmgNotchFreq = deviceInfo.emgNotchFreq;
          offlineBox.EmgMode = deviceInfo.emgMode;
          offlineBox.Region = deviceInfo.region;

          this.boxes = [offlineBox];
        }
        catch (e) {
          this.logger.error(`offline device info error ${JSON.stringify(e)}`, [{'SourceContext': 'AppStateProvider'}, {'Method': 'doOffline'}]);
        }
        this.activeBoxStatus = BoxStatus.ONLINE_ALL;
        if(!this.activeDoctor.ScanningDisabled) this.scanningEnabled = true;
      } else {
        this.activeBoxStatus = BoxStatus.OFFLINE_ALL;
      }
    }
  }

  public async retryBox() {
    this.logger.debug(`box disconnected, retrying (status ${BoxStatus[this.activeBoxStatus]})`, [{'SourceContext': 'AppStateProvider'}, {'Method': 'retryBox'}]);

    // Disable scanning functions in app
    this.scanningEnabled = false;

    // Go to home
    this.events.publish(ClaEvents.NAV_GO_HOME);

    // Box status is unknown
    this.activeBoxStatus = BoxStatus.CONNECTING;

    // Try to reconnect with direct ping
    await this.connectToBox(this.activeBox, true);
  }

  public async connectToBoxById(id: string) {
    // Refresh our boxes
    await this.refreshBoxes();

    // Try and find it
    let box = this.boxes.filter(b => b.DeviceId == id)[0];

    // Connect
    if (box) {
      await this.connectToBox(box);
      // Start the idle watcher
      this.setTimeoutEnabled(true);
    }

  }

  // Status for a connected box. While REST calls still go to the bridge (Local Online), keep
  // ONLINE_LOCAL so checkBox() pings the cloud and switches back via useCloud().
  private connectedBoxStatus(): BoxStatus {
    return this.api.LocalOnlineState.getValue() ? BoxStatus.ONLINE_LOCAL : BoxStatus.ONLINE_ALL;
  }

  public async connectToBox(box: Box, shouldPing: boolean = false) {
    if(box === undefined) return;
    // Disable scanning functions in app
    this.scanningEnabled = false;

    // Save the current box
    this._activeBoxId = box.DeviceId;

    // Save to storage too
    await this.storage.set(ClaStorage.APP_PREVIOUS_BOX_ID, box.DeviceId);

    // clear bluetooth
    if (this.bluetooth !== bluetoothMode.noBT) {
      this.bluetooth = bluetoothMode.noBT;
      await this.storage.remove(ClaStorage.BLUETOOTH);
    }

    // Go to home
    //this.events.publish(ClaEvents.NAV_GO_HOME);

    // Box status is unkown
    this.activeBoxStatus = BoxStatus.CONNECTING;

    // Check if the box is online in azure
    let azureOnline = false;

    // If we are handling a retry, we will want to ping the box from the server
    if (shouldPing) {

      try {
        azureOnline = await this.api.pingBox(this.activeBox.DeviceId);
      }
      catch (e) {
        azureOnline = false;
      }
    } else if(!this.offlineMode) {
      // Otherwise, we just want to get its last known state
      let latestBox: Box;
      try {
        latestBox = await this.api.getBoxById(box.DeviceId).toPromise();
        azureOnline = latestBox.Connected;
      }
      catch (e) {
        azureOnline = false;
      }
    } else {
      azureOnline = true;
    }

    // If the box is online with the cloud
    if (azureOnline) {

      // Ping box locally
      let localOnline = await this.api.pingBoxLocal(box,this.offlineMode);
      if(!localOnline) {
        localOnline = await this.api.pingBoxLocalIp(box,this.offlineMode);
      }
      //  let localOnline = true;

      // If the box is also reachable locally
      if (localOnline) {

        // Last Step: Try to connect to box
        let boxConnected = await this.apiBox.connectToBox(box);

        if (boxConnected) {

          // Box is online and good to go
          this.activeBoxStatus = this.connectedBoxStatus();

          // Enables scanning functions in app
          if(!this.activeDoctor.ScanningDisabled) this.scanningEnabled = true;
        } else {
          // Retry once, then switch to unavailable
          if (await this.apiBox.connectToBox(box)) {

            // Box is online and good to go
            this.activeBoxStatus = this.connectedBoxStatus();

            // Enables scanning functions in app
            if(!this.activeDoctor.ScanningDisabled) this.scanningEnabled = true;
          } else {
            // Switch to unavailable

            // Box is online in azure, but not reachable locally
            this.activeBoxStatus = BoxStatus.ONLINE_AZURE;
          }
        }

      } else {
        // Box is online in azure, but not reachable locally
        this.activeBoxStatus = BoxStatus.ONLINE_AZURE;
      }
    } else {
      // Box is offline completely - check also bridgeless
      this.activeBoxStatus = BoxStatus.OFFLINE_ALL;
      // this.activeBoxStatus = BoxStatus.BRIDGELESS_MODE;
    }
    // If we are in a error state, show the help modal
    this.tryShowHelpModal();
  }

  public clearActiveBox() {
    this._activeBoxId = '';
  }

  public async returnOnline() {
    try {
      // Shutdown the WFD server to make app reconnect to main wifi
      await this.apiBox.onboardingFinish(this.activeDoctor);
    }
    catch (e) {
      this.logger.error(`could not connect to box ${JSON.stringify(e)}`, [{'SourceContext': 'AppStateProvider'}, {'Method': 'returnOnline'}]);
      let alert: Alert;
      alert = this.alertCtrl.create({
        title: 'Return To Online Mode',
        message: 'Please be sure to reconnect your wifi to your normal network for online mode use',
        buttons: [{
          text: 'Okay'
        }]
      });
      await alert.present();
    }
    await this.logOut();
    await delay(3000);
    await this.goOnline();
  }

}



// WEBPACK FOOTER //
// ./src/providers/app-state/app-state.ts